import { Box, Checkbox, Radio, RadioGroup, FormControlLabel, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { normalizeKey } from "../../../../../Helper/Common/common_helpers";

const CheckBoxFormControlLabel = styled(FormControlLabel)({
    margin: '0px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    '& .MuiTypography-root': {
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: '11px',
        lineHeight: '16px',
        color: '#3D4B5A',
    },
    '& .MuiCheckbox-root': {
        padding: '0px',
        marginRight: '4px',
    },
    '& .MuiCheckbox-root.Mui-checked': {
        color: '#6D72F6 !important',
    },
});

const RadioFormControlLabel = styled(FormControlLabel)({
    margin: '0px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    '& .MuiTypography-root': {
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: '11px',
        lineHeight: '16px',
        color: '#3D4B5A',
    },
    '& .MuiRadio-root': {
        padding: '0px',
        marginRight: '6px',
    },
    '& .MuiRadio-root.Mui-checked': {
        color: '#6D72F6 !important',
    },
});

const StyledCheckbox = styled(Checkbox)({
    padding: '4px',
    borderRadius: '4px',
    color: '#9F9F9F',
    '&:hover': {
        backgroundColor: '#F8F7FF',
    },
    '&.Mui-checked': {
        color: '#6D72F6',
    },
    '& .MuiSvgIcon-root': {
        fontSize: 16,
    },
});

const StyledRadio = styled(Radio)({
    padding: '4px',
    color: '#9F9F9F',
    '&:hover': {
        backgroundColor: '#F8F7FF',
    },
    '&.Mui-checked': {
        color: '#6D72F6',
    },
    '& .MuiSvgIcon-root': {
        fontSize: 16,
    },
});

const CompositeFilterBase = ({ filter, newFilters, onChange, onRadioChange }) => {
    const { t } = useTranslation(['common', 'generic_table', 'filters']);
    const subFilters = filter.subFilters || [];

    const isChecked = (subFilterKey, optionId) => {
        return newFilters?.[subFilterKey]?.includes(optionId) || false;
    };

    const getRadioValue = (subFilterKey, options) => {
        const values = newFilters?.[subFilterKey];
        if (values && values.length > 0) {
            try {
                const parsed = JSON.parse(values[0]);
                // Find the option that matches this rangeValue
                const matchingOption = options.find(opt =>
                    JSON.stringify(opt.rangeValue) === JSON.stringify(parsed)
                );
                return matchingOption?.id || '';
            } catch {
                // Fallback for non-JSON values
                return values[0];
            }
        }
        return '';
    };

    const renderMultiSelectOptions = (subFilter) => {
        const options = subFilter.staticData || [];

        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, overflow: 'auto' }}>
                {options.map((option, index) => {
                    const checked = isChecked(subFilter.key, option.id);
                    return (
                        <Box
                            key={index}
                            sx={{
                                borderRadius: '6px',
                                padding: '6px 10px',
                                minWidth: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                cursor: 'pointer',
                                backgroundColor: checked ? '#F4F5FF' : '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: checked ? '#F4F5FF' : '#F8F7FF',
                                    borderColor: '#D1D5DB',
                                },
                            }}
                            onClick={() => {
                                const event = {
                                    target: {
                                        checked: !checked
                                    }
                                };
                                onChange(subFilter.key, option.id)(event);
                            }}
                        >
                            <CheckBoxFormControlLabel
                                control={
                                    <StyledCheckbox
                                        size="small"
                                        checked={checked}
                                    />
                                }
                                label={t(`filters:${normalizeKey(option.value)}`, option.value)}
                                sx={{
                                    m: 0,
                                    pointerEvents: 'none',
                                    '& .MuiTypography-root': {
                                        fontSize: '11px',
                                        fontWeight: checked ? 500 : 400,
                                        color: checked ? '#6D72F6' : '#3D4B5A',
                                    },
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderSingleSelectOptions = (subFilter) => {
        const options = subFilter.staticData || [];
        const currentValue = getRadioValue(subFilter.key, options);

        return (
            <RadioGroup
                value={currentValue}
                onChange={(event) => {
                    const selectedOption = options.find(opt => opt.id === event.target.value);
                    onRadioChange(subFilter.key, selectedOption);
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {options.map((option, index) => {
                        const selected = currentValue === option.id;
                        return (
                            <Box
                                key={index}
                                sx={{
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    backgroundColor: selected ? '#F4F5FF' : '#FFFFFF',
                                    border: '1px solid',
                                    borderColor: selected ? '#6D72F6' : '#E5E7EB',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: selected ? '#F4F5FF' : '#F8F7FF',
                                        borderColor: selected ? '#6D72F6' : '#D1D5DB',
                                    },
                                }}
                                onClick={() => onRadioChange(subFilter.key, option)}
                            >
                                <RadioFormControlLabel
                                    value={option.id}
                                    control={
                                        <StyledRadio
                                            size="small"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    label={t(`filters:${normalizeKey(option.id)}`, option.id)}
                                    sx={{
                                        m: 0,
                                        '& .MuiTypography-root': {
                                            fontSize: '11px',
                                            fontWeight: selected ? 500 : 400,
                                            color: selected ? '#6D72F6' : '#3D4B5A',
                                        },
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </RadioGroup>
        );
    };

    const renderSubFilter = (subFilter) => {
        if (subFilter.displayType === 'singleSelectOptions') {
            return renderSingleSelectOptions(subFilter);
        }
        return renderMultiSelectOptions(subFilter);
    };

    return (
        <Box sx={{ width: '100%' }}>
            {subFilters.map((subFilter, index) => (
                <Box key={subFilter.key || index} sx={{ mb: 1 }}>
                    <Typography variant="fw500s12lh24c5E758D" sx={{ mb: 0.5 }}>
                        {t(`filters:${normalizeKey(subFilter.label)}`, subFilter.label)}
                    </Typography>
                    {renderSubFilter(subFilter)}
                </Box>
            ))}
        </Box>
    );
};

export default CompositeFilterBase;
